import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { getRole, isAuthenticated, onAuthChange } from './lib/auth'
import AppLayout from './components/AppLayout'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import ExpensesPage from './pages/ExpensesPage'
import PayrollPage from './pages/PayrollPage'
import RosterPage from './pages/RosterPage'
import MasterListPage from './pages/MasterListPage'
import RollCallPage from './pages/RollCallPage'
import CalendarPage from './pages/CalendarPage'
import TagsPage from './pages/TagsPage'

/** Admin-only routes — viewers get redirected to /roster. */
const ADMIN_ONLY_ROUTES = new Set(['/dashboard', '/expenses', '/payroll', '/master-list', '/tags'])

/**
 * Auth guard — redirects unauthenticated users to /login.
 * Also redirects viewers away from admin-only routes to /roster.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const authed = isAuthenticated()
  const location = useLocation()
  const role = getRole()

  if (!authed) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Viewers can only access /roster, /rollcall (and / for the index redirect)
  if (role === 'viewer' && ADMIN_ONLY_ROUTES.has(location.pathname)) {
    return <Navigate to="/roster" replace />
  }

  return <>{children}</>
}

/** Redirects to the correct landing page based on role. */
function HomeRedirect() {
  const role = getRole()
  if (role === 'viewer') {
    return <Navigate to="/roster" replace />
  }
  return <Navigate to="/dashboard" replace />
}

export default function App() {
  // Auth is already hydrated at module level in auth.ts (line 35)
  const [ready] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  // React to auth changes (e.g. logout from any page)
  useEffect(() => {
    const unsub = onAuthChange((authed) => {
      if (!authed && location.pathname !== '/login') {
        navigate('/login', { replace: true })
      }
    })
    return unsub
  }, [navigate, location.pathname])

  if (!ready) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<HomeRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="master-list" element={<MasterListPage />} />
        <Route path="roster" element={<RosterPage />} />
        <Route path="rollcall" element={<RollCallPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="tags" element={<TagsPage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  )
}
